import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BiDownload, BiErrorCircle, BiPlay, BiTimeFive, BiVideo, BiVideoOff } from "react-icons/bi";
import axios from "../../../axios";
import SpinnerLoader from "../../../components/UI/SpinnerLoader/SpinnerLoader";
import useMediaLinkConversion, { MEDIA_LINK_PHASE, VIDEO_LINK_URLS } from "../../../hooks/useMediaLinkConversion";
import * as url from "../../../utils/url";
import { isSignedS3Url, canBrowserPlay, MediaLinkCard, DownloadWithNote } from "../MediaLink/mediaLinkShared";

// Bug930: landing page for hashtag video short links — /v/:code, texted and
// emailed to hashtag admins. No login. The BE resolves the code to a fresh
// short-lived URL. An MP4/WebM this browser can really decode gets Play, which
// opens the file in the browser's own player with no conversion, and Download.
// Any other format, or a file that fails the check below, gets a Play button
// that converts the video to MP4 and plays it here. A conversion that already
// started or finished is picked up without a press.

// canPlayType only judges the container (Chrome says "maybe" for 3GP and then
// can't decode the MPEG-4/AMR inside), so a hidden, muted <video> loads just
// the metadata to see whether this browser can decode the file.
const VIDEO_PROBE_TIMEOUT_MS = 3000;
const MEDIA_ERR_DECODE = 3;
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;

// Resolves "playable" (metadata loaded with a picture), "unplayable" (decode /
// format error, or no picture track), or "unknown" (network error, or no answer
// in time, e.g. phones that load nothing before a tap). Returns { promise, cancel }.
const probeVideoPlayback = (videoUrl) => {
  const video = document.createElement("video");
  let timer = null;
  let finish = () => {};

  const cleanup = () => {
    clearTimeout(timer);
    video.removeEventListener("loadedmetadata", handleMetadata);
    video.removeEventListener("error", handleError);
    // Stops the download this probe started.
    video.removeAttribute("src");
    try {
      video.load();
    } catch (e) {
      // nothing left to stop
    }
  };

  const handleMetadata = () => {
    finish(video.videoWidth > 0 ? "playable" : "unplayable");
  };

  const handleError = () => {
    const code = video.error ? video.error.code : 0;
    if (code === MEDIA_ERR_DECODE || code === MEDIA_ERR_SRC_NOT_SUPPORTED) {
      finish("unplayable");
    } else {
      finish("unknown");
    }
  };

  const promise = new Promise((resolve) => {
    let settled = false;
    finish = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };
  });

  video.muted = true;
  video.preload = "metadata";
  video.setAttribute("playsinline", "");
  video.addEventListener("loadedmetadata", handleMetadata);
  video.addEventListener("error", handleError);
  timer = setTimeout(() => finish("unknown"), VIDEO_PROBE_TIMEOUT_MS);
  video.src = videoUrl;

  return { promise, cancel: () => finish("cancelled") };
};

const VideoLinkRedirect = ({ match }) => {
  const code = match.params.code;
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [needsPlayPress, setNeedsPlayPress] = useState(false);
  const [canPlayDirectly, setCanPlayDirectly] = useState(false);
  const [isConversionActive, setIsConversionActive] = useState(false);
  const [initialState, setInitialState] = useState(null);
  const { phase, linkState, retryAfterSeconds, retry, refresh } = useMediaLinkConversion(
    code,
    isConversionActive,
    VIDEO_LINK_URLS
  );
  const refreshedUrlRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    let probe = null;
    axios
      .get(url.resolveVideoLinkPublic(code))
      .then((res) => {
        if (cancelled) return;
        const state = res.data && res.data.data;
        const videoUrl = state && state.url;
        if (!isSignedS3Url(videoUrl)) {
          setIsUnavailable(true);
          return;
        }
        setInitialState(state);
        if (state.status !== "NOT_REQUESTED") {
          setIsConversionActive(true);
          return;
        }
        // Fast path: a format we convert, or the browser already says it can't play this type at all.
        if (state.conversionRequired || !canBrowserPlay(videoUrl, "video")) {
          setNeedsPlayPress(true);
          return;
        }
        probe = probeVideoPlayback(videoUrl);
        probe.promise.then((result) => {
          if (cancelled || result === "cancelled") return;
          if (result === "unplayable") {
            setNeedsPlayPress(true);
          } else {
            // "playable", or "unknown": no clear answer, so let the viewer try the file itself.
            setCanPlayDirectly(true);
          }
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.log(error);
        setIsUnavailable(true);
      });
    return () => {
      cancelled = true;
      if (probe) probe.cancel();
    };
  }, [code]);

  const currentState = linkState || initialState;
  const convertedUrl =
    phase === MEDIA_LINK_PHASE.READY && linkState && isSignedS3Url(linkState.url) ? linkState.url : "";
  const originalDownloadUrl =
    currentState && isSignedS3Url(currentState.originalDownloadUrl) ? currentState.originalDownloadUrl : "";

  // The page may have sat open past the signed URL's hour, so Play reads a fresh one.
  const openInBrowserPlayer = () => {
    const fallbackUrl = initialState.url;
    axios
      .get(url.resolveVideoLinkPublic(code))
      .then((res) => {
        const state = res.data && res.data.data;
        window.location.assign(state && isSignedS3Url(state.url) ? state.url : fallbackUrl);
      })
      .catch(() => window.location.assign(fallbackUrl));
  };

  // An expired signed MP4 URL is re-read once from the status API; nothing is re-converted.
  const handlePlayerError = () => {
    if (refreshedUrlRef.current === convertedUrl) return;
    refreshedUrlRef.current = convertedUrl;
    refresh();
  };

  const downloadOriginalLink = originalDownloadUrl ? (
    <DownloadWithNote href={originalDownloadUrl} className="media_link_secondary_link">
      <BiDownload className="media_link_button_icon" />
      Download original
    </DownloadWithNote>
  ) : null;

  if (
    isUnavailable ||
    phase === MEDIA_LINK_PHASE.UNAVAILABLE ||
    (phase === MEDIA_LINK_PHASE.READY && !convertedUrl)
  ) {
    return (
      <MediaLinkCard
        icon={<BiVideoOff />}
        muted
        title="Video not available"
        text="This video link is invalid or no longer available. The hashtag may have been removed."
      >
        <Link to="/" className="media_link_button">
          Open Trivvy
        </Link>
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.READY) {
    return (
      <MediaLinkCard icon={<BiVideo />} title="Video ready" text="Tap play to watch.">
        <video
          controls
          autoPlay
          playsInline
          preload="metadata"
          src={convertedUrl}
          className="video_link_player"
          onError={handlePlayerError}
        >
          Your browser does not support the video element.
        </video>
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.PROCESSING) {
    return (
      <MediaLinkCard icon={<BiVideo />} title="Video is processing..." text="Converting video to play in your browser. This can take a moment.">
        <div className="media_link_loader">
          <SpinnerLoader />
        </div>
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.STALLED) {
    return (
      <MediaLinkCard icon={<BiTimeFive />} title="Still processing" text="This video is taking longer than usual. Check again in a little while.">
        <button type="button" className="media_link_button" onClick={refresh}>
          Check again
        </button>
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.BUSY) {
    return (
      <MediaLinkCard
        icon={<BiTimeFive />}
        title="Video processing is busy"
        text={
          retryAfterSeconds
            ? "Please try again in " + retryAfterSeconds + " seconds."
            : "Please try again shortly."
        }
      >
        <button type="button" className="media_link_button" onClick={retry}>
          Retry
        </button>
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.FAILED || phase === MEDIA_LINK_PHASE.ERROR) {
    const canRetry = phase === MEDIA_LINK_PHASE.ERROR || (linkState && linkState.canRetry);
    return (
      <MediaLinkCard
        icon={<BiErrorCircle />}
        muted
        title="Video preview unavailable"
        text="We couldn't prepare this video for your browser. You can download the original to watch it on your device."
      >
        {canRetry ? (
          <button type="button" className="media_link_button" onClick={retry}>
            Retry
          </button>
        ) : null}
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (needsPlayPress && phase === MEDIA_LINK_PHASE.IDLE) {
    return (
      <MediaLinkCard icon={<BiVideo />} title="Video ready" text="This video needs to be prepared before it can play in your browser.">
        <button type="button" className="media_link_button" onClick={() => setIsConversionActive(true)}>
          <BiPlay className="media_link_button_icon" />
          Play video
        </button>
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (canPlayDirectly && phase === MEDIA_LINK_PHASE.IDLE) {
    return (
      <MediaLinkCard icon={<BiVideo />} title="Video ready" text="Play the video in your browser or download it.">
        <button type="button" className="media_link_button" onClick={openInBrowserPlayer}>
          <BiPlay className="media_link_button_icon" />
          Play video
        </button>
        {originalDownloadUrl ? (
          <DownloadWithNote href={originalDownloadUrl} className="media_link_secondary_link">
            <BiDownload className="media_link_button_icon" />
            Download video
          </DownloadWithNote>
        ) : null}
      </MediaLinkCard>
    );
  }

  return (
    <MediaLinkCard icon={<BiVideo />} title="Opening video…" text="Your video will start in a moment.">
      <div className="media_link_loader">
        <SpinnerLoader />
      </div>
    </MediaLinkCard>
  );
};

export default VideoLinkRedirect;
